import React, { useState, useEffect, useCallback } from 'react';
import { collection, onSnapshot, query, where, deleteDoc, doc } from 'firebase/firestore';
import { auth, db } from '../../firebaseConfig';
import { useToast } from '../../components/CustomToast';
import Sidebar from '../../partials/Sidebar';
import Header from '../../partials/Header';
import SearchForm from '../../partials/actions/SearchForm';

function UsersTabs() {
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [analyses, setAnalyses] = useState([]);
  const [selectedAnalysis, setSelectedAnalysis] = useState(null);
  const { addToast } = useToast();

  // Close modal on Escape key press
  const handleEscapeKey = useCallback((e) => {
    if (e.key === 'Escape') setSelectedAnalysis(null);
  }, []);

  // Delete analysis function
  const handleDelete = async (id, e) => {
    e.stopPropagation();

    const currentUser = auth.currentUser;
    if (!currentUser) {
      addToast("Du skal være logget ind for at slette analyser", "error");
      return;
    }

    try {
      await Promise.all([
        deleteDoc(doc(db, 'stockAnalyses', id)),
        deleteDoc(doc(db, 'users', currentUser.uid, 'tabs', id))
      ]);

      setSelectedAnalysis(null);
      setAnalyses(prev => prev.filter(analysis => analysis.id !== id));
      addToast("Analyse slettet", "success");
    } catch (err) {
      console.error("Delete error:", err);
      addToast("Fejl ved sletning af analyse", "error");
    }
  };

  // Close modal when clicking outside it
  const handleClickOutside = (e) => {
    if (e.target.classList.contains('modal-overlay')) {
      setSelectedAnalysis(null);
    }
  };

  useEffect(() => {
    document.addEventListener('keydown', handleEscapeKey);
    return () => document.removeEventListener('keydown', handleEscapeKey);
  }, [handleEscapeKey]);

  
  useEffect(() => {
    if (!auth.currentUser) return;
  
    const analysesQuery = query(
      collection(db, 'stockAnalyses'),
      where('userId', '==', auth.currentUser.uid)
    );
  
    const unsubscribe = onSnapshot(
      analysesQuery,
      (snapshot) => {
        const data = snapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        }));
        console.log("Hentede data:", data); // Log data her
        setAnalyses(data);
      },
      (error) => {
        console.error("Firestore error:", error);
        addToast("Fejl ved indlæsning af analyser", "error");
      }
    );
    return () => unsubscribe();
  }, [addToast]);


  return (
    <div className="flex h-[100dvh] overflow-hidden">
      <Sidebar sidebarOpen={sidebarOpen} setSidebarOpen={setSidebarOpen} />
      
      <div className="relative flex flex-col flex-1 overflow-y-auto overflow-x-hidden">
        <Header sidebarOpen={sidebarOpen} setSidebarOpen={setSidebarOpen} />

        <main className="grow">
        <div className="px-4 sm:px-6 lg:px-8 py-8 w-full mx-auto max-w-screen-2xl">
            <div className="mb-8">
              <h2 className="text-2xl font-bold text-gray-800 dark:text-gray-100 mb-4">
                Ai aktieanalyser
              </h2>
              <SearchForm />
            </div>

            {/* Grid container forced to 1 column by default and 3 columns on computer view */}
            <div className="flex-grow">
              {analyses.length === 0 ? (
                <p className="text-gray-600 dark:text-gray-400">
                  {auth.currentUser ? 'Ingen analyser endnu.' : 'Log ind for at se dine analyser.'}
                </p>
              ) : (
                <div className="grid grid-cols-1 gap-4 w-full lg:grid-cols-2 xl:grid-cols-3">
                  {analyses.map((analysis) => (
                    <div 
                      key={analysis.id}
                      onClick={() => setSelectedAnalysis(analysis)}
                      className="bg-white dark:bg-gray-800 rounded-lg shadow-md hover:shadow-lg transition-shadow duration-200 cursor-pointer"
                    >
                      <div className="p-4">
                        <div className="flex justify-between items-center">
                          <div className="w-[calc(100%-32px)]">
                            <h3 className="text-lg font-semibold text-gray-800 dark:text-gray-100 truncate">
                              {analysis.query}
                            </h3>
                            <p className="text-sm text-gray-500 dark:text-gray-400">
                              AI Analyse - {analysis.timestamp?.toDate().toLocaleDateString()}
                            </p>
                          </div>
                          <button
                            onClick={(e) => handleDelete(analysis.id, e)}
                            className="text-red-500 hover:text-red-600 dark:hover:text-red-400 p-1 shrink-0"
                          >
                            <span className="sr-only">Slet</span>
                            <svg className="w-4 h-4 fill-current" viewBox="0 0 16 16">
                              <path d="M5 7h2v6H5V7zm4 0h2v6H9V7zm3-6v2h4v2h-1v10c0 .6-.4 1-1 1H2c-.6 0-1-.4-1-1V5H0V3h4V1c0-.6.4-1 1-1h6c.6 0 1 .4 1 1zM6 2v1h4V2H6zm7 3H3v9h10V5z" />
                            </svg>
                          </button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </main>

        {selectedAnalysis && (
          <div 
            className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 modal-overlay"
            onClick={handleClickOutside}
          >
            <div className="bg-white dark:bg-gray-800 rounded-lg p-6 max-w-2xl w-full mx-4 max-h-[90vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
              <div className="flex justify-between items-start mb-4">
                <div>
                  <h2 className="text-xl font-bold text-gray-800 dark:text-gray-100">
                    {selectedAnalysis.query}
                  </h2>
                  <p className="text-sm text-gray-500 dark:text-gray-400">
                    AI Analyse - {selectedAnalysis.timestamp?.toDate().toLocaleDateString()}
                  </p>
                </div>
                <div className="flex gap-2">
                  <button
                    onClick={() => setSelectedAnalysis(null)}
                    className="text-gray-400 hover:text-gray-500 dark:hover:text-gray-300"
                  >
                    <span className="sr-only">Luk</span>
                    <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                </div>
              </div>
              <div className="text-gray-600 dark:text-gray-300 whitespace-pre-line">
                {selectedAnalysis.analysis}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default UsersTabs;
